import classnames from 'classnames';
import type {
  ChangeEvent,
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  FunctionComponent,
  ReactNode,
} from 'react';

import { Text } from '../Text/Text';

import styles from './Toggle.module.scss';

type BaseProps = Omit<
  ComponentPropsWithoutRef<'label'>,
  'htmlFor' | 'className' | 'children' | 'onChange'
>;

export type ToggleProps = BaseProps & {
  /**
   * input is checked
   */
  checked: boolean;
  /**
   * id of the input and htmlFor in label
   */
  id: string;
  /**
   * Component is disabled
   */
  disabled?: boolean;
  /**
   * To override the style
   */
  className?: string;
  /**
   * input onChange
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /**
   * label
   */
  children?: string | ReactNode;
};

/** @deprecated Use `@/core/tamagoshiTailwind/components/Switch/Switch` instead */
export const Toggle: FunctionComponent<ToggleProps> = ({
  id,
  children,
  className,
  disabled,
  checked,
  onChange,
  ...restProps
}) => {
  const labelClassName = classnames(className, styles.switch, {
    [styles.disabled]: disabled,
  });

  const slider = classnames(styles.slider, styles.round);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    if (onChange) {
      onChange(event);
    }
  };

  return (
    <label {...restProps} className={labelClassName} htmlFor={id}>
      <input
        type="checkbox"
        id={id}
        disabled={disabled}
        checked={checked}
        onChange={handleChange}
      />
      <span className={slider} />
      {children && (
        <Text tag="span" type="body2" className={styles.children}>
          {children}
        </Text>
      )}
    </label>
  );
};

Toggle.displayName = 'Toggle';
